import type { InvoicePart } from '../../../types/invoice';

function isTeslaVendor(vendor: string | null | undefined): boolean {
    return (vendor ?? '').toLowerCase().includes('tesla');
}

/** Mirrors API isCorePart (partClassification): core charges, deposit lines, Tesla -C suffix, etc. */
export function isCorePartForArithmetic(
    part: InvoicePart | undefined,
    vendorName?: string | null,
): boolean {
    if (!part) return false;
    const isTeslaInvoice = isTeslaVendor(vendorName);
    const partNumber = part.partNumber;

    if (isTeslaInvoice && partNumber && typeof partNumber === 'string' && /-C$/i.test(partNumber.replace(/\s/g, ''))) {
        return true;
    }

    if (partNumber && typeof partNumber === 'string' && partNumber.toUpperCase().includes('CORE')) {
        return true;
    }
    const title = part.title;
    if (title) {
        const titleLower = title.toLowerCase().trim();
        if (titleLower === 'core' || titleLower === 'core price' || titleLower === 'core charge' || titleLower.startsWith('core ')) {
            return true;
        }
        if (/\bdeposit\b/.test(titleLower)) return true;
    }

    const list = Math.abs(part.listPrice ?? 0);
    const buy = Math.abs(part.unitBuyPrice ?? 0);

    if (partNumber && typeof partNumber === 'string' && /-C$/i.test(partNumber.replace(/\s/g, ''))) {
        if (/\bcore\b/i.test(title ?? '')) return true;
        if (list < 0.01) return true;
        return false;
    }

    if (title) {
        const titleLower = title.toLowerCase().trim();
        if (titleLower.includes('core')) {
            if (list < 0.01 || buy < 0.01) return true;
            if (Math.abs(list - buy) < 0.01 && buy >= 0.01) return true;
        }
    }
    return false;
}

export function isLikelyFreightTitle(title: string | null | undefined): boolean {
    const t = (title ?? '').toLowerCase();
    if (!t) return false;
    const compact = t.replace(/[\s\-_]/g, '');
    return (
        /\bfreight\b/.test(t) ||
        /\bshipping\b/.test(t) ||
        /\bdelivery\b/.test(t) ||
        /\btransport(?:ation)?\b/.test(t) ||
        /\bship(?:ping)?\s*(?:fee|charge)\b/.test(t) ||
        /\b(fedex|ups|usps|dhl)\b/.test(t) ||
        /\bstandard\s+shipping\b/.test(t) ||
        /\bovernight\b/.test(t) ||
        /\bexpedited\b/.test(t) ||
        /\bsame[\s-]day\b/.test(t) ||
        compact.includes('fuelcharge') ||
        compact.includes('fuelfee') ||
        compact.includes('fuelsurcharge') ||
        (compact.includes('fuel') && compact.includes('charge'))
    );
}

export function isFreightPart(partNumber: string | null | undefined): boolean {
    if (!partNumber || typeof partNumber !== 'string') return false;
    const n = partNumber.replace(/[\s\-_]/g, '').toLowerCase();
    return (
        n === 'freight' ||
        n.startsWith('freight') ||
        n === 'shipping' ||
        n === 'delivery' ||
        n === 'ovn' ||
        n.includes('freight') ||
        n === 'fuel' ||
        n.startsWith('fuel') ||
        n.includes('fuel')
    );
}

export function isFreightLineItem(partNumber: string | null | undefined, title?: string | null | undefined): boolean {
    return isFreightPart(partNumber) || isLikelyFreightTitle(title);
}

export function isFuelPart(partNumber: string | null | undefined, title?: string | null): boolean {
    if (partNumber && typeof partNumber === 'string') {
        const n = partNumber.replace(/[\s\-_]/g, '').toLowerCase();
        if (n === 'fuel' || n.startsWith('fuel') || n.includes('fuel')) return true;
    }
    const compact = (title ?? '').replace(/[\s\-_]/g, '').toLowerCase();
    return (
        compact.includes('fuelcharge') ||
        compact.includes('fuelfee') ||
        compact.includes('fuelsurcharge') ||
        (compact.includes('fuel') && compact.includes('charge'))
    );
}

export function isMiscFeePart(
    partNumber: string | null | undefined,
    title?: string | null,
    totalBuyPrice?: number | null,
): boolean {
    const n = (partNumber ?? '').replace(/[\s\-_]/g, '').toLowerCase();
    const t = (title ?? '').replace(/[\s\-_]/g, '').toLowerCase();
    if (!n && !t) return false;
    if (typeof totalBuyPrice === 'number' && Number.isFinite(totalBuyPrice) && Math.abs(totalBuyPrice) >= 100) return false;
    const source = `${n} ${t}`;
    return source.includes('restock') || source.includes('fee') || source.includes('surcharge') || source.includes('servicecharge');
}
